/**
 * Comprehensive Realistic Test Data Generators
 * Provides industry-specific, geographically diverse, and realistic test data
 * for Cali.AI testing scenarios across different business verticals.
 */

// ============= COMPANY NAME GENERATORS =============

const COMPANY_PREFIXES = [
  'Advanced', 'Premier', 'Elite', 'Professional', 'Excellence', 'Superior', 'Prime',
  'Innovative', 'Dynamic', 'Strategic', 'Optimal', 'Pinnacle', 'Apex', 'Summit'
];

const REAL_ESTATE_TERMS = [
  'Realty', 'Real Estate', 'Properties', 'Homes', 'Estates', 'Residences',
  'Housing', 'Investments', 'Land Co', 'Home Sales', 'Property Group', 'Realty Partners'
];

const AUTOMOTIVE_TERMS = [
  'Motors', 'Auto Group', 'Car Dealership', 'Vehicle Sales', 'Auto Center',
  'Automotive', 'Cars & Trucks', 'Auto Plaza', 'Motor Company', 'Auto Mall', 'Vehicle Center'
];

const HEALTHCARE_TERMS = [
  'Medical Center', 'Family Practice', 'Health Clinic', 'Medical Group',
  'Healthcare Associates', 'Wellness Center', 'Medical Associates', 'Health Partners',
  'Care Center', 'Medical Solutions', 'Health Services', 'Medical Practice'
];

const ECOMMERCE_TERMS = [
  'Online Store', 'E-commerce', 'Digital Shop', 'Web Store', 'Online Retail',
  'Digital Commerce', 'Online Solutions', 'E-Shop', 'Digital Marketplace', 'Web Commerce'
];

const PROFESSIONAL_TERMS = [
  'Consulting', 'Services', 'Solutions', 'Associates', 'Partners', 'Group',
  'Advisors', 'Professionals', 'Experts', 'Specialists', 'Consultants', 'Firm'
];

// ============= GEOGRAPHIC DATA =============

interface CityData {
  city: string;
  state: string;
  stateCode: string;
  areaCode: string;
  timezone: string;
  region: string;
}

const US_CITIES: CityData[] = [
  // Northeast
  { city: 'New York', state: 'New York', stateCode: 'NY', areaCode: '212', timezone: 'EST', region: 'northeast' },
  { city: 'Brooklyn', state: 'New York', stateCode: 'NY', areaCode: '718', timezone: 'EST', region: 'northeast' },
  { city: 'Manhattan', state: 'New York', stateCode: 'NY', areaCode: '646', timezone: 'EST', region: 'northeast' },
  { city: 'Boston', state: 'Massachusetts', stateCode: 'MA', areaCode: '617', timezone: 'EST', region: 'northeast' },
  { city: 'Philadelphia', state: 'Pennsylvania', stateCode: 'PA', areaCode: '215', timezone: 'EST', region: 'northeast' },
  { city: 'Washington', state: 'District of Columbia', stateCode: 'DC', areaCode: '202', timezone: 'EST', region: 'northeast' },
  
  // Southeast
  { city: 'Miami', state: 'Florida', stateCode: 'FL', areaCode: '305', timezone: 'EST', region: 'southeast' },
  { city: 'Tampa', state: 'Florida', stateCode: 'FL', areaCode: '813', timezone: 'EST', region: 'southeast' },
  { city: 'Orlando', state: 'Florida', stateCode: 'FL', areaCode: '407', timezone: 'EST', region: 'southeast' },
  { city: 'Atlanta', state: 'Georgia', stateCode: 'GA', areaCode: '404', timezone: 'EST', region: 'southeast' },
  { city: 'Charlotte', state: 'North Carolina', stateCode: 'NC', areaCode: '704', timezone: 'EST', region: 'southeast' },
  { city: 'Nashville', state: 'Tennessee', stateCode: 'TN', areaCode: '615', timezone: 'CST', region: 'southeast' },
  
  // West Coast
  { city: 'Los Angeles', state: 'California', stateCode: 'CA', areaCode: '213', timezone: 'PST', region: 'west' },
  { city: 'San Francisco', state: 'California', stateCode: 'CA', areaCode: '415', timezone: 'PST', region: 'west' },
  { city: 'San Diego', state: 'California', stateCode: 'CA', areaCode: '619', timezone: 'PST', region: 'west' },
  { city: 'Oakland', state: 'California', stateCode: 'CA', areaCode: '510', timezone: 'PST', region: 'west' },
  { city: 'San Jose', state: 'California', stateCode: 'CA', areaCode: '408', timezone: 'PST', region: 'west' },
  { city: 'Seattle', state: 'Washington', stateCode: 'WA', areaCode: '206', timezone: 'PST', region: 'west' },
  { city: 'Portland', state: 'Oregon', stateCode: 'OR', areaCode: '503', timezone: 'PST', region: 'west' },
  
  // Midwest
  { city: 'Chicago', state: 'Illinois', stateCode: 'IL', areaCode: '312', timezone: 'CST', region: 'midwest' },
  { city: 'Detroit', state: 'Michigan', stateCode: 'MI', areaCode: '313', timezone: 'EST', region: 'midwest' },
  { city: 'Cleveland', state: 'Ohio', stateCode: 'OH', areaCode: '216', timezone: 'EST', region: 'midwest' },
  { city: 'Milwaukee', state: 'Wisconsin', stateCode: 'WI', areaCode: '414', timezone: 'CST', region: 'midwest' },
  { city: 'Minneapolis', state: 'Minnesota', stateCode: 'MN', areaCode: '612', timezone: 'CST', region: 'midwest' },
  
  // Southwest
  { city: 'Phoenix', state: 'Arizona', stateCode: 'AZ', areaCode: '602', timezone: 'MST', region: 'southwest' },
  { city: 'Las Vegas', state: 'Nevada', stateCode: 'NV', areaCode: '702', timezone: 'PST', region: 'southwest' },
  { city: 'Denver', state: 'Colorado', stateCode: 'CO', areaCode: '303', timezone: 'MST', region: 'southwest' },
  { city: 'Austin', state: 'Texas', stateCode: 'TX', areaCode: '512', timezone: 'CST', region: 'southwest' },
  { city: 'Houston', state: 'Texas', stateCode: 'TX', areaCode: '713', timezone: 'CST', region: 'southwest' },
  { city: 'Dallas', state: 'Texas', stateCode: 'TX', areaCode: '214', timezone: 'CST', region: 'southwest' },
  { city: 'San Antonio', state: 'Texas', stateCode: 'TX', areaCode: '210', timezone: 'CST', region: 'southwest' }
];

// ============= BUSINESS EMAIL DOMAINS =============

const BUSINESS_EMAIL_DOMAINS = [
  // Technology
  'techcorp.com', 'innovatetech.com', 'digitalstorm.com', 'cloudware.net', 'datastream.io',
  'nexustech.com', 'quantumsoft.com', 'cyberflow.net', 'bitforge.com', 'synthwave.io',
  
  // Professional Services  
  'consultpro.com', 'advisorgroup.net', 'expertservices.com', 'strategicmind.com', 'eliteadvice.net',
  'primeadvisors.com', 'summitconsult.com', 'apexpartners.com', 'pinnaclegroup.net', 'visionaryservices.com',
  
  // Real Estate
  'premierhomes.com', 'eliterealty.net', 'dreamhomes.com', 'luxuryestates.net', 'propertypros.com',
  'homefinderplus.com', 'realestateexcellence.net', 'propertyexperts.com', 'estateleaders.net', 'homeadvantage.com',
  
  // Healthcare
  'medicalexcellence.com', 'healthfirst.net', 'carepartners.com', 'wellnesscenter.org', 'healthprofessionals.net',
  'medicalpractice.com', 'caregivers.net', 'healthservices.org', 'medicalgroup.com', 'wellnesspartners.net',
  
  // Automotive
  'autoprofessionals.com', 'vehicleexperts.net', 'carmasters.com', 'autoexcellence.net', 'motorgroup.com',
  'autodealer.net', 'carcentral.com', 'vehiclesolutions.net', 'autoadvantage.com', 'motorexperts.net',
  
  // E-commerce
  'onlineshopping.com', 'digitalcommerce.net', 'e-retailpro.com', 'webstoreplus.net', 'onlinemarket.com',
  'digitalstore.net', 'e-commerceexp.com', 'webretail.net', 'onlinebusiness.com', 'digitaltrading.net'
];

// ============= REALISTIC NAMES =============

const FIRST_NAMES = [
  'Michael', 'Sarah', 'David', 'Jennifer', 'Robert', 'Lisa', 'John', 'Mary',
  'Christopher', 'Patricia', 'Matthew', 'Linda', 'Anthony', 'Elizabeth', 'Mark', 'Barbara',
  'Donald', 'Susan', 'Steven', 'Jessica', 'Paul', 'Ashley', 'Andrew', 'Kimberly',
  'Joshua', 'Amy', 'Kenneth', 'Angela', 'Kevin', 'Emma', 'Brian', 'Olivia',
  'George', 'Sophia', 'Edward', 'Chloe', 'Ronald', 'Isabella', 'Timothy', 'Mia',
  'Jason', 'Madison', 'Jeffrey', 'Emily', 'Ryan', 'Abigail', 'Jacob', 'Charlotte'
];

const LAST_NAMES = [
  'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis',
  'Rodriguez', 'Martinez', 'Hernandez', 'Lopez', 'Gonzalez', 'Wilson', 'Anderson', 'Thomas',
  'Taylor', 'Moore', 'Jackson', 'Martin', 'Lee', 'Perez', 'Thompson', 'White',
  'Harris', 'Sanchez', 'Clark', 'Ramirez', 'Lewis', 'Robinson', 'Walker', 'Young',
  'Allen', 'King', 'Wright', 'Scott', 'Torres', 'Nguyen', 'Hill', 'Flores',
  'Green', 'Adams', 'Nelson', 'Baker', 'Hall', 'Rivera', 'Campbell', 'Mitchell'
];

// ============= CAMPAIGN DATA =============

interface CampaignTemplate {
  name: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign?: string;
  description: string;
}

const CAMPAIGN_TEMPLATES: Record<string, CampaignTemplate[]> = {
  real_estate: [
    {
      name: 'Luxury Home Showcase',
      utmSource: 'google',
      utmMedium: 'cpc',
      utmCampaign: 'luxury-homes',
      description: 'High-end property marketing campaign'
    },
    {
      name: 'First Time Buyer Program',
      utmSource: 'facebook',
      utmMedium: 'social',
      utmCampaign: 'first-time-buyers',
      description: 'Targeting first-time home buyers'
    },
    {
      name: 'Investment Property Leads',
      utmSource: 'linkedin',
      utmMedium: 'cpc',
      utmCampaign: 'investment-properties',
      description: 'Commercial and investment property marketing'
    },
    {
      name: 'Quick Sale Properties',
      utmSource: 'bing',
      utmMedium: 'cpc',
      utmCampaign: 'quick-sale',
      description: 'Fast-moving property listings'
    }
  ],
  automotive: [
    {
      name: 'New Car Summer Sale',
      utmSource: 'google',
      utmMedium: 'cpc',
      utmCampaign: 'summer-auto-sale',
      description: 'Seasonal new vehicle promotions'
    },
    {
      name: 'Certified Pre-Owned',
      utmSource: 'facebook',
      utmMedium: 'social',
      utmCampaign: 'certified-preowned',
      description: 'Used vehicle certification program'
    },
    {
      name: 'Service Department Promo',
      utmSource: 'email',
      utmMedium: 'email',
      utmCampaign: 'service-special',
      description: 'Auto service and maintenance marketing'
    },
    {
      name: 'Lease Special Offers',
      utmSource: 'radio',
      utmMedium: 'traditional',
      utmCampaign: 'lease-specials',
      description: 'Vehicle leasing promotions'
    }
  ],
  healthcare: [
    {
      name: 'Annual Wellness Checkups',
      utmSource: 'google',
      utmMedium: 'cpc',
      utmCampaign: 'wellness-checkup',
      description: 'Preventive care appointment scheduling'
    },
    {
      name: 'New Patient Welcome',
      utmSource: 'facebook',
      utmMedium: 'social',
      utmCampaign: 'new-patients',
      description: 'New patient acquisition campaign'
    },
    {
      name: 'Specialist Referrals',
      utmSource: 'direct',
      utmMedium: 'referral',
      utmCampaign: 'specialist-network',
      description: 'Healthcare specialist referral program'
    },
    {
      name: 'Telehealth Services',
      utmSource: 'email',
      utmMedium: 'email',
      utmCampaign: 'telehealth',
      description: 'Remote healthcare consultation promotion'
    }
  ],
  ecommerce: [
    {
      name: 'Holiday Shopping Event',
      utmSource: 'google',
      utmMedium: 'cpc',
      utmCampaign: 'holiday-shopping',
      description: 'Seasonal e-commerce promotions'
    },
    {
      name: 'Customer Support Line',
      utmSource: 'direct',
      utmMedium: 'organic',
      utmCampaign: 'customer-support',
      description: 'Customer service call tracking'
    },
    {
      name: 'Product Launch Campaign',
      utmSource: 'instagram',
      utmMedium: 'social',
      utmCampaign: 'product-launch',
      description: 'New product introduction marketing'
    },
    {
      name: 'Abandoned Cart Recovery',
      utmSource: 'email',
      utmMedium: 'email',
      utmCampaign: 'cart-recovery',
      description: 'E-commerce cart abandonment follow-up'
    }
  ],
  professional: [
    {
      name: 'Business Consultation Leads',
      utmSource: 'linkedin',
      utmMedium: 'cpc',
      utmCampaign: 'business-consulting',
      description: 'Professional consulting services marketing'
    },
    {
      name: 'Legal Services Inquiry',
      utmSource: 'google',
      utmMedium: 'cpc',
      utmCampaign: 'legal-services',
      description: 'Legal consultation appointment scheduling'
    },
    {
      name: 'Financial Planning Services',
      utmSource: 'facebook',
      utmMedium: 'social',
      utmCampaign: 'financial-planning',
      description: 'Financial advisory service promotion'
    },
    {
      name: 'Tax Preparation Services',
      utmSource: 'email',
      utmMedium: 'email',
      utmCampaign: 'tax-prep',
      description: 'Seasonal tax preparation marketing'
    }
  ]
};

// ============= GENERATOR FUNCTIONS =============

/**
 * Generate realistic company name for specific industry
 */
export function generateCompanyName(industry: string = 'general'): string {
  const prefix = COMPANY_PREFIXES[Math.floor(Math.random() * COMPANY_PREFIXES.length)];
  const randomSuffix = Math.floor(Math.random() * 999) + 1;
  
  let terms: string[];
  switch (industry.toLowerCase()) {
    case 'real_estate':
      terms = REAL_ESTATE_TERMS;
      break;
    case 'automotive':
      terms = AUTOMOTIVE_TERMS;
      break;
    case 'healthcare':
      terms = HEALTHCARE_TERMS;
      break;
    case 'ecommerce':
      terms = ECOMMERCE_TERMS;
      break;
    case 'professional':
      terms = PROFESSIONAL_TERMS;
      break;
    default:
      terms = ['Solutions', 'Services', 'Group', 'Company', 'Corp', 'LLC'];
  }
  
  const suffix = terms[Math.floor(Math.random() * terms.length)];
  return `${prefix} ${suffix}`;
}

/**
 * Generate realistic business email address
 */
export function generateBusinessEmail(firstName?: string, lastName?: string, industry?: string): string {
  const fName = firstName || FIRST_NAMES[Math.floor(Math.random() * FIRST_NAMES.length)];
  const lName = lastName || LAST_NAMES[Math.floor(Math.random() * LAST_NAMES.length)];
  
  // Filter domains by industry if specified
  let domains = BUSINESS_EMAIL_DOMAINS;
  if (industry) {
    const industryKeywords = {
      real_estate: ['realty', 'homes', 'estate', 'property'],
      automotive: ['auto', 'car', 'motor', 'vehicle'],
      healthcare: ['medical', 'health', 'care', 'wellness'],
      ecommerce: ['online', 'digital', 'e-commerce', 'web'],
      technology: ['tech', 'digital', 'cyber', 'data']
    };
    
    const keywords = industryKeywords[industry as keyof typeof industryKeywords] || [];
    const industryDomains = domains.filter(domain => 
      keywords.some(keyword => domain.toLowerCase().includes(keyword))
    );
    
    if (industryDomains.length > 0) {
      domains = industryDomains;
    }
  }
  
  const domain = domains[Math.floor(Math.random() * domains.length)];
  const emailFormats = [
    `${fName.toLowerCase()}.${lName.toLowerCase()}@${domain}`,
    `${fName.toLowerCase()}${lName.toLowerCase()}@${domain}`,
    `${fName.charAt(0).toLowerCase()}${lName.toLowerCase()}@${domain}`,
    `${fName.toLowerCase()}.${lName.charAt(0).toLowerCase()}@${domain}`
  ];
  
  return emailFormats[Math.floor(Math.random() * emailFormats.length)];
}

/**
 * Generate realistic phone number with geographic context
 */
export function generatePhoneNumber(region?: string, areaCode?: string): { phone: string; city: CityData } {
  let cityData: CityData;
  
  if (areaCode) {
    const matchingCity = US_CITIES.find(city => city.areaCode === areaCode);
    cityData = matchingCity || US_CITIES[Math.floor(Math.random() * US_CITIES.length)];
  } else if (region) {
    const regionCities = US_CITIES.filter(city => city.region === region);
    cityData = regionCities.length > 0 
      ? regionCities[Math.floor(Math.random() * regionCities.length)]
      : US_CITIES[Math.floor(Math.random() * US_CITIES.length)];
  } else {
    cityData = US_CITIES[Math.floor(Math.random() * US_CITIES.length)];
  }
  
  const exchange = Math.floor(Math.random() * 743) + 200; // 200-942 (valid exchange codes)
  const subscriber = Math.floor(Math.random() * 9000) + 1000; // 1000-9999
  
  return {
    phone: `+1${cityData.areaCode}${exchange}${subscriber}`,
    city: cityData
  };
}

/**
 * Generate realistic Twilio Account SID
 */
export function generateTwilioSid(prefix: string = 'AC'): string {
  const chars = '0123456789abcdef';
  let result = prefix;
  for (let i = 0; i < 32; i++) {
    result += chars[Math.floor(Math.random() * chars.length)];
  }
  return result;
}

/**
 * Generate realistic user profile
 */
export interface UserProfile {
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  role: 'business_owner' | 'agency' | 'admin';
  company: string;
  phone: string;
  city: CityData;
}

export function generateUserProfile(industry?: string, role: 'business_owner' | 'agency' | 'admin' = 'business_owner'): UserProfile {
  const firstName = FIRST_NAMES[Math.floor(Math.random() * FIRST_NAMES.length)];
  const lastName = LAST_NAMES[Math.floor(Math.random() * LAST_NAMES.length)];
  const company = generateCompanyName(industry);
  const email = generateBusinessEmail(firstName, lastName, industry);
  const phoneData = generatePhoneNumber();
  
  return {
    firstName,
    lastName,
    fullName: `${firstName} ${lastName}`,
    email,
    role,
    company,
    phone: phoneData.phone,
    city: phoneData.city
  };
}

/**
 * Generate realistic campaign data for specific industry
 */
export function generateCampaignData(industry: string = 'general'): CampaignTemplate {
  const templates = CAMPAIGN_TEMPLATES[industry] || CAMPAIGN_TEMPLATES.professional;
  const template = templates[Math.floor(Math.random() * templates.length)];
  
  // Add realistic variations
  const variations = [
    'Q1 2024', 'Q2 2024', 'Spring', 'Summer', 'Fall', 'Winter',
    'Premium', 'Standard', 'Express', 'Deluxe', 'Pro', 'Elite'
  ];
  
  const variation = variations[Math.floor(Math.random() * variations.length)];
  
  return {
    ...template,
    name: `${template.name} - ${variation}`,
    utmCampaign: `${template.utmCampaign}-${variation.toLowerCase().replace(' ', '-')}`
  };
}

/**
 * Generate realistic ad data for campaigns
 */
export interface AdTemplate {
  name: string;
  description: string;
  targetAudience: string;
  callToAction: string;
}

export function generateAdData(industry: string = 'general', campaignName?: string): AdTemplate {
  const adTemplates: Record<string, AdTemplate[]> = {
    real_estate: [
      { name: 'Dream Home Finder', description: 'Find your perfect home today', targetAudience: 'Home buyers 25-45', callToAction: 'Schedule a Showing' },
      { name: 'Investment Opportunity', description: 'Prime real estate investments', targetAudience: 'Real estate investors', callToAction: 'Get Property Analysis' },
      { name: 'Luxury Living', description: 'Exclusive luxury properties', targetAudience: 'High-income buyers', callToAction: 'Book Private Tour' }
    ],
    automotive: [
      { name: 'New Car Deals', description: 'Unbeatable prices on new vehicles', targetAudience: 'Car shoppers 18-65', callToAction: 'Schedule Test Drive' },
      { name: 'Certified Pre-Owned', description: 'Quality used vehicles with warranty', targetAudience: 'Budget-conscious buyers', callToAction: 'View Inventory' },
      { name: 'Service Special', description: 'Expert auto maintenance services', targetAudience: 'Vehicle owners', callToAction: 'Book Service Appointment' }
    ],
    healthcare: [
      { name: 'Wellness Checkup', description: 'Comprehensive health screenings', targetAudience: 'Adults 30+', callToAction: 'Schedule Appointment' },
      { name: 'Family Practice', description: 'Complete family healthcare', targetAudience: 'Families with children', callToAction: 'Become a Patient' },
      { name: 'Specialist Care', description: 'Expert medical specialists', targetAudience: 'Referred patients', callToAction: 'Get Consultation' }
    ],
    ecommerce: [
      { name: 'Flash Sale Alert', description: 'Limited time offers on top products', targetAudience: 'Online shoppers', callToAction: 'Shop Now' },
      { name: 'Customer Support', description: '24/7 customer assistance', targetAudience: 'Existing customers', callToAction: 'Contact Support' },
      { name: 'New Product Launch', description: 'Be first to try our latest products', targetAudience: 'Early adopters', callToAction: 'Pre-Order Now' }
    ]
  };
  
  const templates = adTemplates[industry] || [
    { name: 'Professional Services', description: 'Expert consultation and support', targetAudience: 'Business professionals', callToAction: 'Get Quote' }
  ];
  
  const template = templates[Math.floor(Math.random() * templates.length)];
  const suffix = Math.floor(Math.random() * 99) + 1;
  
  return {
    ...template,
    name: campaignName ? `${template.name} - ${campaignName}` : `${template.name} ${suffix}`
  };
}

/**
 * Generate password with varying complexity levels
 */
export function generatePassword(complexity: 'weak' | 'medium' | 'strong' = 'medium'): string {
  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const numbers = '0123456789';
  const symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?';
  
  switch (complexity) {
    case 'weak':
      return 'password123'; // Intentionally weak for testing validation
    case 'medium':
      const mediumChars = lowercase + uppercase + numbers;
      return Array.from({ length: 10 }, () => 
        mediumChars[Math.floor(Math.random() * mediumChars.length)]
      ).join('');
    case 'strong':
      const strongChars = lowercase + uppercase + numbers + symbols;
      let password = '';
      password += lowercase[Math.floor(Math.random() * lowercase.length)]; // Ensure lowercase
      password += uppercase[Math.floor(Math.random() * uppercase.length)]; // Ensure uppercase
      password += numbers[Math.floor(Math.random() * numbers.length)]; // Ensure number
      password += symbols[Math.floor(Math.random() * symbols.length)]; // Ensure symbol
      
      // Fill rest randomly
      for (let i = 4; i < 16; i++) {
        password += strongChars[Math.floor(Math.random() * strongChars.length)];
      }
      
      // Shuffle the password
      return password.split('').sort(() => Math.random() - 0.5).join('');
    default:
      return 'TestPassword123!';
  }
}

/**
 * Generate industry-specific test scenario
 */
export interface IndustryScenario {
  industry: string;
  companyProfile: UserProfile;
  campaigns: CampaignTemplate[];
  ads: AdTemplate[];
  phoneNumbers: { phone: string; city: CityData; purpose: string }[];
  businessDescription: string;
}

export function generateIndustryScenario(industry: string): IndustryScenario {
  const companyProfile = generateUserProfile(industry);
  const campaigns = Array.from({ length: Math.floor(Math.random() * 3) + 2 }, () => 
    generateCampaignData(industry)
  );
  const ads = campaigns.flatMap(campaign => 
    Array.from({ length: Math.floor(Math.random() * 2) + 1 }, () => 
      generateAdData(industry, campaign.name)
    )
  );
  
  const phoneNumbers = Array.from({ length: Math.floor(Math.random() * 3) + 1 }, () => {
    const phoneData = generatePhoneNumber();
    const purposes = ['Main Office', 'Sales Line', 'Support Line', 'Emergency Line', 'After Hours'];
    return {
      ...phoneData,
      purpose: purposes[Math.floor(Math.random() * purposes.length)]
    };
  });
  
  const businessDescriptions: Record<string, string> = {
    real_estate: 'Full-service real estate agency specializing in residential and commercial properties with expert agents and comprehensive market knowledge.',
    automotive: 'Premier automotive dealership offering new and certified pre-owned vehicles with professional service department and financing options.',
    healthcare: 'Modern healthcare practice providing comprehensive medical services with experienced physicians and state-of-the-art facilities.',
    ecommerce: 'Growing online retail business offering curated products with excellent customer service and fast shipping nationwide.',
    professional: 'Established professional services firm delivering expert consultation and solutions to businesses across multiple industries.'
  };
  
  return {
    industry,
    companyProfile,
    campaigns,
    ads,
    phoneNumbers,
    businessDescription: businessDescriptions[industry] || 'Professional business providing quality services to valued customers.'
  };
}

/**
 * Get all available cities for geographic testing
 */
export function getAllCities(): CityData[] {
  return [...US_CITIES];
}

/**
 * Get cities by region
 */
export function getCitiesByRegion(region: string): CityData[] {
  return US_CITIES.filter(city => city.region === region);
}

/**
 * Get available area codes
 */
export function getAreaCodes(): string[] {
  return [...new Set(US_CITIES.map(city => city.areaCode))];
}

/**
 * Generate realistic forwarding number based on business location
 */
export function generateForwardingNumber(businessCity?: CityData): string {
  const city = businessCity || US_CITIES[Math.floor(Math.random() * US_CITIES.length)];
  const phoneData = generatePhoneNumber(city.region, city.areaCode);
  return phoneData.phone;
}